// src/Pages/Roles.jsx
import React, { useEffect, useMemo, useState } from "react";
import PageShell from "../Layout/PageShell";
import Topbar from "../Layout/Topbar";
import "../Home/Home.css"; // reuse Home header/toolbar/card styles
import "./lists.css";      // light table/modal helpers
import { addRole, deleteRole, editRole, getRoles } from "../Api/Api";

// Permissions based on actions in Home.js
const ALL_PERMS = [
  { key: "createUser", label: "Add User" },
  { key: "editUser", label: "Edit User" },
  { key: "toggleStatus", label: "Activate/Deactivate" },
  { key: "renew", label: "Renew" },
  { key: "loginAsClient", label: "Login as Client" },
  { key: "sandboxToken", label: "Sandbox Token" },
  { key: "productionToken", label: "Production Token" },
  { key: "toggleApproval", label: "Enable/Disable Approval" },
  { key: "toggleProductionMode", label: "Enable/Disable Production" },
];

const PERMISSION_ALIASES = {
  createUser: ["createUser", "addUser"],
  addUser: ["addUser", "createUser"],
};

const unwrapList = (res) => {
  const response = res?.data?.response;
  if (Array.isArray(response?.detail)) return response.detail;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  if (Array.isArray(res?.data?.detail)) return res.data.detail;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

const normalizePermissions = (permissions) => {
  if (!permissions) return [];
  if (Array.isArray(permissions)) {
    return permissions
      .map((permission) =>
        typeof permission === "string"
          ? permission
          : permission?.key ?? permission?.permission ?? permission?.permission_key ?? permission?.name ?? permission?.value
      )
      .filter(Boolean)
      .map(String);
  }
  if (typeof permissions === "string") {
    try {
      const parsed = JSON.parse(permissions);
      return normalizePermissions(parsed);
    } catch (_) {
      return permissions.split(",").map((item) => item.trim()).filter(Boolean);
    }
  }
  if (typeof permissions === "object") {
    return Object.entries(permissions)
      .flatMap(([key, value]) => {
        if (value === true || value === 1 || value === "1" || value === "true") return [key];
        if (value === false || value === 0 || value === "0" || value === "false" || value == null) return [];
        return normalizePermissions(value);
      })
      .filter(Boolean)
      .map(String);
  }
  return [];
};

const hasPerm = (permissions, key) => {
  const aliases = PERMISSION_ALIASES[key] || [key];
  return aliases.some((alias) => permissions.includes(alias));
};

const mapRole = (role) => ({
  id: role.role_id ?? role.id,
  name: role.role_name ?? role.name ?? "",
  perms: normalizePermissions(role.permissions ?? role.perms ?? role.role_permissions ?? role.rolePermissions),
});

/* -------------------- Component -------------------- */
export default function Roles() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState("");

  // editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [perms, setPerms] = useState([]);

  const refresh = async () => {
    setReloading(true);
    setError("");
    try {
      const res = await getRoles();
      setRows(unwrapList(res).map(mapRole).filter((role) => role.id));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load roles.");
    } finally {
      setReloading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? rows.filter((r) => r.name.toLowerCase().includes(s)) : rows;
  }, [rows, q]);

  const openNew = () => {
    setEditingId(null);
    setName("");
    setPerms([]);
    setShowEditor(true);
  };

  const openEdit = (role) => {
    setEditingId(role.id);
    setName(role.name);
    setPerms(role.perms.slice());
    setShowEditor(true);
  };

  const togglePerm = (key) => {
    setPerms((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  };

  const onSave = async () => {
    const roleName = name.trim();
    if (!roleName) {
      alert("Role name is required");
      return;
    }
    try {
      if (editingId) {
        await editRole({ role_id: editingId, role_name: roleName, permissions: perms });
      } else {
        await addRole({ role_name: roleName, permissions: perms });
      }
      setShowEditor(false);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save role.");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this role?")) return; // eslint-safe
    try {
      await deleteRole(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete role.");
    }
  };

  return (
    <PageShell>
      {/* Top header identical to Home.js */}
      <Topbar onRefresh={refresh} refreshing={reloading} />
      {error && <div className="inline-error">{error}</div>}

      {/* Toolbar identical layout to Home.js + forced spacing to match */}
      <div
        className="toolbar"
        style={{ margin: "14px 0", padding: "0 20px", gap: 10 }}
      >
        <div className="search">
          <input
            type="text"
            placeholder="Search…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="right-controls" style={{ display: "flex", gap: 10 }}>
          <button className="btn primary" type="button" onClick={openNew}>
            + Add Role
          </button>
        </div>
      </div>

      {/* Content card */}
      <div className="card">
        <div className="card-head">
          <h3>Roles</h3>
          <div className="chips">
            <span className="chip">Total: {rows.length}</span>
            <span className="chip ok">Shown: {filtered.length}</span>
          </div>
        </div>

        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Name</th>
                <th>Permissions</th>
                <th style={{ width: 120, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>
                    <div className="chips" style={{ gap: 6, flexWrap: "wrap" }}>
                      {r.perms.map((k) => (
                        <span key={k} className="chip small">
                          {ALL_PERMS.find((p) => p.key === k)?.label || k}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      className="icon"
                      title="Edit"
                      onClick={() => openEdit(r)}
                    >
                      ✏️
                    </button>
                    <button
                      className="icon"
                      title="Delete"
                      onClick={() => onDelete(r.id)}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}

              {filtered.length === 0 && (
                <tr>
                  <td colSpan={3} className="empty">
                    No roles
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Role Modal */}
     {showEditor && (
  <div className="modal">
    <div className="dialog pro">
      <div className="dialog-head">
        <h4>{editingId ? "Edit Role" : "Add Role"}</h4>
        <p className="muted">Create a role and choose what this role is allowed to do.</p>
      </div>

      <div className="form-grid single">
        <div className="field">
          <label>Role Name <span className="req">*</span></label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Manager"
          />
          {!name.trim() && <small className="hint">Role name is required.</small>}
        </div>
      </div>

      <fieldset className="perm-fieldset">
        <legend>Permissions</legend>

        <div className="perm-toolbar">
          <button
            type="button"
            className="btn light sm"
            onClick={() => setPerms(ALL_PERMS.map(p => p.key))}
          >
            Select all
          </button>
          <button
            type="button"
            className="btn light sm"
            onClick={() => setPerms([])}
          >
            Clear
          </button>
        </div>

        <div className="perm-chips">
          {ALL_PERMS.map((p) => {
            const checked = hasPerm(perms, p.key);
            return (
              <label key={p.key} className={`perm-chip ${checked ? "on" : ""}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => togglePerm(p.key)}
                />
                <span>{p.label}</span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className="dialog-actions">
        <button
          className="btn primary"
          onClick={onSave}
          disabled={!name.trim()}
        >
          Save
        </button>
        <button className="btn light" onClick={() => setShowEditor(false)}>
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

    </PageShell>
  );
}
